import DeviceDropdown from '../dashboard/DeviceDropdown';
import IntervalButtons from '../dashboard/IntervalButtons';
import LoadingMessage from '../common/LoadingMessage';
import ErrorMessage from '../common/ErrorMessage';
import CrashChart from './CrashChart';
import { useCrashReportData } from '../../hooks/useCrashReportData';

function CrashDashboard({
    selectedDevice,
    onDeviceChange,
    selectedInterval,
    customRange,
    onIntervalChange,
    onRangeChange,
}) {
    const { reports, meta, loading, error, lastUpdated, refresh } = useCrashReportData(
        selectedDevice,
        selectedInterval,
        customRange
    );
    const hasSelection = Boolean(selectedDevice);
    const hasData = Array.isArray(reports) && reports.length > 0;

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px' }}>
                <h2>Crash Reports</h2>
                <p className="section-intro">
                    Inspect crash reports captured for the selected device within the chosen time range. Each crash event
                    is grouped by reported cause so that you can identify trends and spikes quickly.
                </p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Data analysis controls</div>
                        <DeviceDropdown selectedDevice={selectedDevice} onDeviceChange={onDeviceChange} />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Data interval</div>
                        <IntervalButtons
                            selectedInterval={selectedInterval}
                            onIntervalChange={onIntervalChange}
                            customRange={customRange}
                            onCustomRangeChange={onRangeChange}
                        />
                    </div>
                </div>
            </section>

            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button type="button" onClick={refresh} disabled={!hasSelection || loading} className="refresh-button">
                    🔄 Refresh
                </button>
                {lastUpdated && <span className="refresh-meta">Last updated: {lastUpdated.toLocaleTimeString()}</span>}
                {!hasSelection && !loading && !error && (
                    <span className="refresh-meta">Select a device to load crash data.</span>
                )}
                {meta?.count !== undefined && <span className="refresh-meta">Total crashes: {meta.count}</span>}
            </div>

            {error && (
                <div style={{ marginTop: '20px' }}>
                    <ErrorMessage message={error} title="Failed to load crash reports" />
                </div>
            )}

            {loading && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage
                        message={hasSelection ? 'Loading crash reports…' : 'Select a device to load crash reports.'}
                    />
                </div>
            )}

            {!loading && !error && hasSelection && (
                <div style={{ marginTop: '24px' }}>
                    {hasData ? <CrashChart items={reports} /> : <p>No crash reports detected for the selected period.</p>}
                </div>
            )}
        </div>
    );
}

export default CrashDashboard;
